import { CardShell } from '@/src/components/global/card-shell/CardShell'
import { FamilyCardHeader } from '@/src/components/global/family-card/family-card-header/FamilyCardHeader'
import CardScore from '@/src/components/global/card-score/CardScore'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FamilyDto, PriorityDiagnosisSummaryDto } from '@/infra/api/generated/model'

interface PriorityFamilyCardProps {
  family: FamilyDto
  diagnosis?: PriorityDiagnosisSummaryDto | null
  isExpired?: boolean
  onEvaluateClick: (family: FamilyDto) => void
  onActionClick?: (family: FamilyDto, diagnosis: PriorityDiagnosisSummaryDto) => void
}

const PriorityFamilyCard = ({
  family,
  diagnosis,
  onEvaluateClick,
  onActionClick,
  isExpired = false,
}: PriorityFamilyCardProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'components.priority-family-card' })
  const isToEvaluate = !diagnosis && !isExpired
  const score = diagnosis?.score
  const hasScore = score !== undefined && score !== null

  let handleCardClick: (() => void) | undefined
  if (isToEvaluate) {
    handleCardClick = () => onEvaluateClick(family)
  } else if (hasScore && onActionClick && diagnosis) {
    handleCardClick = () => onActionClick(family, diagnosis)
  }

  return (
    <CardShell
      data-testid="priority-family-card"
      className={`${diagnosis || isExpired ? '' : 'border-l-4 border-l-indigo-700'} ${isToEvaluate || hasScore ? 'cursor-pointer hover:bg-neutral-50 transition-colors' : ''}`}
      onClick={handleCardClick}
    >
      <div className="flex flex-col gap-2">
        <FamilyCardHeader family={family} />
        <div className="flex justify-between items-center">
          {diagnosis && (
            <PriorityFamilyCardInfo author={diagnosis.userId} date={diagnosis.completedAt} />
          )}

          {isToEvaluate && (
            <span className="text-indigo-700 text-sm font-medium" data-testid="to-evaluate-text">
              {t('to-diagnose')}
            </span>
          )}
          <div className="ml-auto">
            <CardScore score={diagnosis?.score} />
          </div>
        </div>
      </div>
    </CardShell>
  )
}

interface PriorityFamilyCardInfoProps {
  author: string
  date?: string | null
}

const PriorityFamilyCardInfo = ({ author, date }: PriorityFamilyCardInfoProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'components.priority-family-card' })
  const formattedDate = useMemo(() => {
    if (!date) {
      return undefined
    }
    const frmDate = new Date(date).toLocaleDateString()
    return author
      ? t('diagnosed-by', {
          author,
          date: frmDate,
        })
      : frmDate
  }, [date, author, t])

  return <span className="text-neutral-500 text-xs">{formattedDate}</span>
}

export default PriorityFamilyCard
